import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TruckAnnouncementService } from '../../core/api/truck-announcement.service';
import { ReferenceDataService } from '../../core/api/reference-data.service';
import { TruckAnnouncement } from '../../shared/models/truck-announcement';
import { LookupOption } from '../../shared/models/lookup-option';
import { statusTone } from '../../shared/status-tone';
import { AppNavComponent } from '../../shared/app-nav.component';
import { BackButtonComponent } from '../../shared/back-button.component';
import { TranslatePipe } from '../../shared/translate.pipe';
import { StatusLabelPipe } from '../../shared/status-label.pipe';
import { MneDatePipe } from '../../shared/mne-date.pipe';
import { I18nService } from '../../shared/i18n.service';

@Component({
  standalone: true,
  imports: [RouterLink, AppNavComponent, BackButtonComponent, TranslatePipe, StatusLabelPipe, MneDatePipe],
  template: `
        <app-nav></app-nav>
    <main class="page-shell">
      <header class="topbar">
        <app-back></app-back>
        <h1>{{ 'ta.fallback' | t }}</h1>
        @if (item()?.Id; as id) { <a class="icon-action" [routerLink]="['/truck-announcements', id, 'edit']">✎</a> }
      </header>
      @if (item(); as a) {
        <section class="detail-card">
          <div class="detail-heading">
            <div>
              <p class="eyebrow">#{{ a.Id }}</p>
              <h2>{{ a.TerminalOperatorName || ('ta.fallback' | t) }}</h2>
            </div>
            <span class="status-pill" [class]="tone(a.TruckAnnouncementStatusName)">{{ (a.TruckAnnouncementStatusName | statusLabel) || ('status.draft' | t) }}</span>
          </div>
          <dl>
            <dt>{{ 'common.warehouse' | t }}</dt><dd>{{ a.WarehouseName || a.WarehouseCode || '—' }}</dd>
            <dt>{{ 'ta.terminalOperator' | t }}</dt><dd>{{ a.TerminalOperatorName || a.TerminalOperatorCode || '—' }}</dd>
            <dt>{{ 'ta.trucksQuantity' | t }}</dt><dd>{{ a.TrucksQuantity ?? '—' }}</dd>
            <dt>{{ 'ta.announcementFrom' | t }}</dt><dd>{{ a.AnnouncementFrom | mneDate }}</dd>
            <dt>{{ 'ta.announcementTo' | t }}</dt><dd>{{ a.AnnouncementTo | mneDate }}</dd>
            <dt>{{ 'ta.cargoDescription' | t }}</dt><dd>{{ a.ParkingCargoDescriptionName || '—' }}</dd>
            <dt>{{ 'ta.description' | t }}</dt><dd>{{ a.Description || '—' }}</dd>
            <dt>{{ 'ta.note' | t }}</dt><dd>{{ a.Note || '—' }}</dd>
            <dt>{{ 'ta.directPortEntrance' | t }}</dt><dd>{{ (a.DirectPortEntrance ? 'common.yes' : 'common.no') | t }}</dd>
            <dt>{{ 'common.createdBy' | t }}</dt><dd>{{ a.CreatedByName || '—' }}</dd>
            <dt>{{ 'ta.licencePlate' | t }}</dt><dd>{{ (a.DirectPortEntrance ? a.FrontVehicleCode : a.LicencePlate) || '—' }}</dd>
            <dt>{{ 'ta.permitUserBadgeNumber' | t }}</dt><dd>{{ badgeLabel(a) }}</dd>
          </dl>

          @if (a.TruckAnnouncementPins?.length) {
            <h3 class="section-title">{{ 'ta.pins' | t }}</h3>
            <section class="list">
              @for (pin of a.TruckAnnouncementPins; track pin.Id) {
                <a class="list-row" [routerLink]="['/truck-announcements', a.Id, 'pins', pin.Id]">
                  <div>
                    <span class="row-id">#{{ pin.Id }}</span>
                    <strong>{{ pin.PIN || ('ta.pinFallback' | t) }}</strong>
                    <span>{{ pin.LicencePlate || pin.RealizedLicencePlate || '' }}</span>
                  </div>
                  <div class="row-meta"><b>›</b></div>
                </a>
              }
            </section>
          }

          <div class="form-actions detail-actions">
            <a class="button secondary" [routerLink]="['/truck-announcements', a.Id, 'edit']">{{ 'common.edit' | t }}</a>
            @if (!isSent(a)) {
              <button class="button" type="button" (click)="send(a)" [disabled]="sending()">{{ sending() ? ('ta.sending' | t) : ('ta.send' | t) }}</button>
            }
          </div>
        </section>
        @if (success()) { <p class="success notice">{{ success() }}</p> }
        @if (error()) { <p class="error notice">{{ error() | t }}</p> }
      } @else if (error()) { <p class="error notice">{{ error() | t }}</p> } @else { <div class="skeleton-detail">
        <div class="skeleton-heading"></div>
        <div class="skeleton-line w-70"></div>
        <div class="skeleton-line w-55"></div>
        <div class="skeleton-line w-85"></div>
        <div class="skeleton-line w-40"></div>
      </div> }
    </main>
  `
})
export class TruckAnnouncementDetailPage implements OnInit {
  readonly item = signal<TruckAnnouncement | null>(null);
  readonly error = signal('');
  readonly success = signal('');
  readonly sending = signal(false);
  readonly permitBadges = signal<LookupOption[]>([]);
  private readonly i18n = inject(I18nService);
  private id = '';

  constructor(
    private readonly route: ActivatedRoute,
    private readonly api: TruckAnnouncementService,
    private readonly refs: ReferenceDataService
  ) {}

  ngOnInit(): void {
    this.id = this.route.snapshot.paramMap.get('id') ?? '';
    if (!this.id) { this.error.set('ta.detailLoadError'); return; }
    this.refs.permitUserBadges().subscribe({ next: badges => this.permitBadges.set(badges), error: () => {} });
    this.load();
  }

  badgeLabel(item: TruckAnnouncement): string {
    if (item.PermitUserBadgeId == null) return '—';
    const badge = this.permitBadges().find(b => b.Id === item.PermitUserBadgeId);
    return badge ? `${badge['Badge'] ?? badge.Code} — ${badge.Name}` : String(item.PermitUserBadgeId);
  }

  send(item: TruckAnnouncement): void {
    if (item.Id == null || this.sending()) return;
    if (item.DirectPortEntrance && !item.PermitUserBadgeId) {
      this.error.set('ta.badgeRequiredForSend');
      return;
    }
    const confirmed = window.confirm(`${this.i18n.translate('ta.sendConfirm')} #${item.Id}?`);
    if (!confirmed) return;
    this.sending.set(true);
    this.error.set('');
    this.success.set('');
    this.api.send(item.Id).subscribe({
      next: () => {
        this.success.set(`${this.i18n.translate('ta.sendSuccess')} #${item.Id}`);
        this.sending.set(false);
        this.load(false);
      },
      error: err => {
        this.error.set(err?.error?.Message ?? err?.error?.message ?? 'ta.sendError');
        this.sending.set(false);
      }
    });
  }

  tone(status?: string | null): string { return statusTone(status); }

  isSent(item: TruckAnnouncement): boolean {
    return (item.TruckAnnouncementStatusName ?? '').trim().toLowerCase() === 'sent';
  }

  private load(clear = true): void {
    if (clear) this.item.set(null);
    this.api.get(this.id).subscribe({
      next: item => this.item.set(item),
      error: err => this.error.set(err?.error?.Message ?? err?.error?.message ?? 'ta.detailLoadError')
    });
  }
}
